
import { h } from 'preact';
import { ASSISTANT_INITIAL, ASSISTANT_NAME } from '../brand.js';

export function BootstrapSteps({ salonName, steps = {}, currentStep, onRefresh, onReviewLocation, refreshing = false, pollingExpired = false, guided = false }) {
  // Ordered list of steps to display
  const displaySteps = [
    { id: 'catalog_fetched', label: 'Fetching your catalog' },
    { id: 'services_categorized', label: 'Categorizing services' },
    { id: 'knowledge_bootstrapped', label: 'Building knowledge base' },
  ];

  // Helper to determine status if not explicitly in steps dict
  const getStepStatus = (stepId) => {
    const stepData = steps[stepId];
    if (stepData) return stepData.status;
    
    // Fallback logic if step data missing but we know current step
    // (Simple logic: if current step index > this step index, it's completed)
    const currentIndex = displaySteps.findIndex(s => s.id === currentStep);
    const thisIndex = displaySteps.findIndex(s => s.id === stepId);
    
    if (currentIndex > thisIndex) return 'completed';
    if (currentStep === stepId) return 'in_progress';
    return 'pending';
  };

  return (
    <div class={guided ? "setup-learning" : "min-h-screen flex flex-col items-center justify-center px-6"} style={{ background: guided ? 'transparent' : 'var(--cream)' }}>
      <div class="text-center max-w-sm w-full">
        {/* Logo */}
        <div
          class="logo mx-auto mb-6"
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'var(--sage)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
          }}
        >
          {ASSISTANT_INITIAL.toLowerCase()}
        </div>

        <div class="logo" style={{ fontSize: '1.5rem', color: 'var(--black)', marginBottom: '0.75rem' }}>
          Setting up {ASSISTANT_NAME}
        </div>

        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '2rem' }}>
          I'm learning about <span style={{ color: 'var(--black)', fontWeight: 500 }}>{salonName || 'your salon'}</span> from Square.
        </p>

        {/* Steps List */}
        <div class="flex flex-col gap-4 text-left max-w-xs mx-auto w-full">
          {displaySteps.map((step) => {
            const status = getStepStatus(step.id);
            const error = steps[step.id]?.last_error;

            return (
              <StepItem 
                key={step.id} 
                label={step.label} 
                status={status} 
                error={error}
              />
            );
          })}
        </div>
        {Object.values(steps).some(step => step?.status === 'failed') && <p role="status" class="text-sm mt-5" style={{ color: 'var(--text-secondary)' }}>Setup needs attention. Refresh checks the current status; it does not restart setup. <a href="mailto:support@gojonathan.ai" style={{ textDecoration: 'underline' }}>Contact support</a> if the problem continues.</p>}
        {pollingExpired && <p role="status" class="text-sm mt-5" style={{ color: 'var(--text-secondary)' }}>Automatic status updates have paused. Refresh to check whether setup has finished.</p>}
        {onRefresh && (!guided || pollingExpired || Object.values(steps).some(step => step?.status === 'failed')) && <button type="button" onClick={onRefresh} disabled={refreshing} style={{ minHeight: '44px', padding: '0.7rem 1rem', marginTop: '1.25rem', border: '1px solid var(--border-subtle)', borderRadius: '12px', background: 'white', cursor: 'pointer', color: 'var(--black)' }}>{refreshing ? 'Refreshing status…' : 'Refresh status'}</button>}
        {onReviewLocation && <button type="button" onClick={onReviewLocation} style={{ display: 'block', margin: '1rem auto', minHeight: '44px', padding: '0.75rem', font: 'inherit' }}>Review Square location</button>}
      </div>
    </div>
  );
}

function StepItem({ label, status, error }) {
  const isFailed = status === 'failed';
  const isCompleted = status === 'completed' || status === 'skipped';
  const isInProgress = status === 'in_progress';

  return (
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-3">
        {/* Icon Container */}
        <div style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isCompleted && (
            <svg style={{ width: '20px', height: '20px', color: 'var(--sage)' }} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          )}
          
          {isInProgress && (
            <div class="animate-spin" style={{ width: '18px', height: '18px', border: '2px solid #e5e7eb', borderTopColor: 'var(--sage)', borderRadius: '50%' }} />
          )}
          
          {isFailed && (
             <svg style={{ width: '20px', height: '20px', color: '#ef4444' }} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}

          {status === 'pending' && (
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#e5e7eb' }} />
          )}
        </div>

        {/* Label */}
        <span style={{ 
          fontSize: '0.9rem', 
          color: isCompleted ? 'var(--black)' : 'var(--text-secondary)',
          fontWeight: isInProgress ? 500 : 400
        }}>
          {label}
        </span>
      </div>

      {/* A terminal error is reported honestly; refreshing never restarts it. */}
      {isFailed && (
        <div class="ml-9 text-xs text-red-500 flex flex-col gap-1">
          <span>{error || 'Something went wrong.'}</span>

        </div>
      )}
    </div>
  );
}
